import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { initOneSignal, oneSignalLogin, oneSignalLogout, setOneSignalEmail } from '@/utils/oneSignalManager';
import { clearGoogleSession } from '@/utils/googleSignIn';
import { restoreSession } from '@/utils/sessionRestore';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

// Token persistence helpers — survive Instagram/Facebook in-app browser localStorage wipes
const TOKEN_KEY = 'bodymax-token';

function persistToken(t) {
  localStorage.setItem(TOKEN_KEY, t);
  sessionStorage.setItem(TOKEN_KEY, t);
  // Cookie fallback — Secure only on HTTPS (production), omit on HTTP (dev)
  const secure = window.location.protocol === 'https:' ? ';Secure' : '';
  document.cookie = `bm_token=${t};path=/;max-age=${7*86400};SameSite=Lax${secure}`;
}

function readToken() {
  // Try localStorage first, then sessionStorage, then cookie
  const ls = localStorage.getItem(TOKEN_KEY);
  if (ls) return ls;
  const ss = sessionStorage.getItem(TOKEN_KEY);
  if (ss) {
    // Re-sync to localStorage
    localStorage.setItem(TOKEN_KEY, ss);
    return ss;
  }
  // Cookie fallback
  const match = document.cookie.match(/(?:^|;\s*)bm_token=([^;]+)/);
  if (match) {
    const ct = match[1];
    localStorage.setItem(TOKEN_KEY, ct);
    sessionStorage.setItem(TOKEN_KEY, ct);
    return ct;
  }
  return null;
}

function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(TOKEN_KEY);
  document.cookie = 'bm_token=;path=/;max-age=0';
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => readToken());
  const [loading, setLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  const api = useMemo(() => {
    const instance = axios.create({ baseURL: API });
    instance.interceptors.request.use((config) => {
      const t = readToken();
      if (t) config.headers.Authorization = `Bearer ${t}`;
      return config;
    });
    return instance;
  }, []);

  const checkSubscription = useCallback(async () => {
    try {
      const res = await api.get('/subscription/status');
      setSubscriptionStatus(res.data);
      return res.data;
    } catch (err) {
      // If error, assume no subscription (new user)
      setSubscriptionStatus({ has_access: false, status: 'no_subscription' });
      return { has_access: false, status: 'no_subscription' };
    }
  }, [api]);

  // Bumped by 'online'/visibility events to retry a session restore that couldn't reach the server.
  const [restoreNonce, setRestoreNonce] = useState(0);
  const [sessionUnreachable, setSessionUnreachable] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    restoreSession(() => api.get('/auth/me'), {
      isCancelled: () => cancelled,
      onRetry: (attempt, err) => console.warn(`[Auth] /auth/me failed (${err?.response?.status || err?.message}) — retry ${attempt}, keeping session`),
    }).then(result => {
      if (cancelled || result.status === 'cancelled') return;
      if (result.status === 'unauthorized') {
        // Only a real 401 ends the session.
        clearToken();
        setToken(null);
        return;
      }
      if (result.status === 'unreachable') {
        // Keep the token; retry when the device comes back online or the app is reopened.
        console.warn('[Auth] Server unreachable — session kept, will retry');
        setSessionUnreachable(true);
        return;
      }
      setSessionUnreachable(false);
      const res = { data: result.user };
      setUser(res.data);
      // Initialize OneSignal and associate with user
      initOneSignal().then(async () => {
        try {
          if (res.data.id) {
            await oneSignalLogin(res.data.id);
            console.log('[Auth] OneSignal login successful for', res.data.id.slice(0, 8));
          }
          if (res.data.email) await setOneSignalEmail(res.data.email);
          // Reconciliation: verify subscription is valid (not local-prefixed)
          const subId = localStorage.getItem('onesignal_subscription_id') || '';
          if (subId.startsWith('local-')) {
            console.warn('[Auth] OneSignal has local-only subscription — push will not work. Re-requesting permission.');
            const { requestPushPermission } = await import('@/utils/oneSignalManager');
            await requestPushPermission();
          }
        } catch (osErr) {
          console.error('[Auth] OneSignal login FAILED — backend cannot target this user for push:', osErr);
        }
      }).catch(err => console.error('[Auth] OneSignal init failed:', err));
      // Check subscription status after getting user
      if (res.data.onboarding_complete) {
        checkSubscription();
      }
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [token, api, checkSubscription, restoreNonce]);

  useEffect(() => {
    if (!sessionUnreachable) return;
    const retry = () => {
      if (document.visibilityState === 'hidden') return;
      setSessionUnreachable(false);
      setRestoreNonce(n => n + 1);
    };
    window.addEventListener('online', retry);
    document.addEventListener('visibilitychange', retry);
    return () => {
      window.removeEventListener('online', retry);
      document.removeEventListener('visibilitychange', retry);
    };
  }, [sessionUnreachable]);

  const login = useCallback(async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    // Clear stale workout data from any previous user on this device
    localStorage.removeItem('bm_active_workout');
    persistToken(res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data.user;
  }, []);

  const googleLogin = useCallback(async (credential, challengeId) => {
    const res = await axios.post(`${API}/auth/google`, {
      credential,
      challenge_id: challengeId,
      signup_source: sessionStorage.getItem('bm_signup_source') || 'google',
    });
    localStorage.removeItem('bm_active_workout');
    persistToken(res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data.user;
  }, []);

  const register = useCallback(async (email, password, name, termsAccepted = false, ageConfirmed = false) => {
    // Detect signup source from URL params or referrer
    const params = new URLSearchParams(window.location.search);
    let signupSource = 'direct';
    if (params.get('fbclid')) signupSource = 'facebook';
    else if (params.get('igshid')) signupSource = 'instagram';
    else if (params.get('gclid')) signupSource = 'google_ads';
    else if (params.get('utm_source')) signupSource = params.get('utm_source');
    else if (document.referrer.includes('google.com')) signupSource = 'google';
    else if (document.referrer.includes('facebook.com') || document.referrer.includes('fb.com')) signupSource = 'facebook';
    else if (document.referrer.includes('instagram.com')) signupSource = 'instagram';
    else if (document.referrer.includes('tiktok.com')) signupSource = 'tiktok';
    else if (document.referrer.includes('twitter.com') || document.referrer.includes('x.com')) signupSource = 'twitter';
    // Also check sessionStorage for source captured on landing
    const storedSource = sessionStorage.getItem('bm_signup_source');
    if (storedSource && signupSource === 'direct') signupSource = storedSource;

    const res = await axios.post(`${API}/auth/register`, { 
      email, 
      password, 
      name,
      terms_accepted: termsAccepted,
      terms_version: "1.0",
      privacy_version: "1.0",
      age_confirmed: ageConfirmed,
      // window.WTN exists in every browser (the bridge script loads on all pages); only isNativeApp is true inside the app.
      entry_source: (typeof window !== 'undefined' && window.WTN?.isNativeApp) ? "android_app" : "web",
      signup_source: signupSource
    });
    // Clear stale workout data from any previous user on this device
    localStorage.removeItem('bm_active_workout');
    persistToken(res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data.user;
  }, []);

  const logout = useCallback(() => {
    oneSignalLogout();
    clearGoogleSession();
    clearToken();
    // Clear all workout/session state so next user doesn't see stale banners
    localStorage.removeItem('bm_active_workout');
    localStorage.removeItem('bm_install_dismissed_at');
    localStorage.removeItem('bm_first_dashboard_at');
    localStorage.removeItem('bm_onboard_step');
    localStorage.removeItem('bm_onboard_form');
    // Clear any exercise index keys
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('bm_active_ex_') || key.startsWith('bm_skip_')) {
        localStorage.removeItem(key);
      }
    });
    setToken(null);
    setUser(null);
    setSubscriptionStatus(null);
  }, []);

  const updateUser = useCallback((data) => {
    setUser(prev => prev ? { ...prev, ...data } : prev);
  }, []);

  const value = useMemo(() => ({
    user, token, loading, login, googleLogin, register, logout, updateUser, api,
    subscriptionStatus, checkSubscription, setUser, setToken
  }), [user, token, loading, login, googleLogin, register, logout, updateUser, api, subscriptionStatus, checkSubscription]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
